/**
 * One conversation for the agent: routing banner (offer / queue / owned by a colleague / unanswered),
 * the UNVERIFIED-mobile warning for site guests with one-tap number correction, the timeline (greeting
 * and notices, internal notes, files, typing / seen), and a composer with canned replies, internal
 * notes, attachments and «ارسال و حل شد». Polls every 2.5 s while visible.
 */
import { cx, formatJalali, formatRelative, useToast } from '@hopex/ui';
import {
  AlertTriangle,
  ArrowRight,
  Check,
  CheckCheck,
  ChevronDown,
  FileText,
  Headset,
  History,
  Lock,
  MoreHorizontal,
  Paperclip,
  Phone,
  SendHorizontal,
  Sparkle,
  Star,
  UserRoundSearch,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api, errorMessage } from '../lib/api.ts';
import { CANNED, CATEGORY_FA, PRIORITY_FA, STATUS_FA } from '../lib/labels.ts';
import { navigate, useVisible } from '../lib/platform.ts';
import type { Attachment, Me, Message, ThreadView } from '../lib/types.ts';
import {
  CountdownRing,
  KindBadge,
  PriorityChip,
  span,
  TrustBadge,
  TypingDots,
  useNow,
  WhoAvatar,
} from './parts.tsx';

export function ThreadScreen({
  id,
  me,
  onOpenUser,
  onChanged,
}: {
  id: string;
  me: Me;
  onOpenUser: (userId: string) => void;
  onChanged: () => void;
}) {
  const [t, setT] = useState<ThreadView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const visible = useVisible();
  const opened = useRef(false);
  const toast = useToast();

  const load = useCallback(async () => {
    try {
      const v = await api.get<ThreadView>(`/threads/${id}${opened.current ? '' : '?opened=1'}`);
      opened.current = true;
      setT(v);
      setError(null);
    } catch (e) {
      setError(errorMessage(e));
    }
  }, [id]);

  useEffect(() => {
    const first = window.setTimeout(() => void load(), 0);
    if (!visible) return () => window.clearTimeout(first);
    const timer = window.setInterval(() => void load(), 2_500);
    return () => {
      window.clearTimeout(first);
      window.clearInterval(timer);
    };
  }, [load, visible]);

  const act = async (path: string, body?: unknown, ok?: string) => {
    try {
      await api.post(`/threads/${id}/${path}`, body);
      if (ok) toast.show({ tone: 'success', title: ok });
      await load();
      onChanged();
    } catch (e) {
      toast.show({ tone: 'danger', title: errorMessage(e) });
      await load();
    }
  };

  if (!t)
    return (
      <div className="grid h-full place-items-center p-8 text-center">
        {error ? (
          <div className="flex flex-col items-center gap-3">
            <p role="alert" className="m-0 text-[13px] text-negative">
              {error}
            </p>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="min-h-10 cursor-pointer rounded-full border border-line px-4 text-[13px]"
            >
              بازگشت به صندوق
            </button>
          </div>
        ) : (
          <TypingDots label="در حال باز کردن گفتگو…" />
        )}
      </div>
    );

  const name =
    t.who.type === 'GUEST' ? `${t.who.firstName} ${t.who.lastName}` : (t.who.name ?? 'کاربر');
  const userId = t.who.type === 'USER' ? t.who.userId : (t.who.matchedUser?.id ?? null);
  const done = t.status === 'RESOLVED' || t.status === 'CLOSED';
  // One owner: reply to my conversation, or to an unowned one (replying claims it). A colleague's
  // conversation takes internal notes only until it is taken over.
  const canWrite = t.routing.mine || !t.routing.assignee;

  return (
    <div className="flex h-full min-h-0 flex-col" data-thread={t.ref}>
      {/* Header */}
      <header className="relative z-30 flex shrink-0 items-center gap-2.5 border-b border-line bg-surface-1/90 px-3 pb-2.5 pt-[calc(env(safe-area-inset-top)+10px)] backdrop-blur lg:pt-2.5">
        <button
          type="button"
          onClick={() => navigate('/')}
          aria-label="بازگشت به صندوق"
          className="hx-press grid size-10 shrink-0 cursor-pointer place-items-center rounded-full text-fg-muted hover:bg-surface-2 lg:hidden"
          data-thread-back
        >
          <ArrowRight aria-hidden className="size-5" />
        </button>
        <WhoAvatar name={name} kind={t.kind} online={t.live.userOnline} size={40} />
        <div className="min-w-0 flex-1">
          <p className="m-0 truncate text-[14.5px] font-extrabold">{name}</p>
          <p className="m-0 flex items-center gap-1.5 truncate text-[11.5px] text-fg-muted">
            {t.live.userTyping ? (
              <TypingDots label="در حال نوشتن…" />
            ) : t.live.userOnline ? (
              <>
                <span className="size-1.5 rounded-full bg-positive" /> آنلاین در صفحه‌ی گفتگو
              </>
            ) : (
              <bdi dir="ltr" className="font-mono">
                {t.ref}
              </bdi>
            )}
          </p>
        </div>
        {userId && (
          <button
            type="button"
            onClick={() => onOpenUser(userId)}
            className="hx-press inline-flex min-h-10 shrink-0 cursor-pointer items-center gap-1.5 rounded-full border border-line-strong bg-surface-2 px-3 text-[12.5px] font-bold text-fg hover:border-accent"
            data-open-360
          >
            <UserRoundSearch aria-hidden className="size-4 text-accent-text" />
            پرونده
          </button>
        )}
        <ThreadMenu t={t} onAct={act} />
      </header>

      {/* Context strip */}
      <div className="flex shrink-0 flex-wrap items-center gap-1.5 border-b border-line bg-surface-1 px-3 py-2">
        <KindBadge kind={t.kind} />
        <TrustBadge verified={t.who.verified} guest={t.who.type === 'GUEST'} />
        <PriorityChip priority={t.priority} />
        <span className="inline-flex h-6 items-center rounded-full bg-surface-2 px-2 text-[11px] font-semibold text-fg-muted">
          {CATEGORY_FA[t.category] ?? t.category}
        </span>
        <span className="inline-flex h-6 items-center rounded-full bg-surface-2 px-2 text-[11px] font-semibold text-fg-muted">
          {STATUS_FA[t.status] ?? t.status}
        </span>
        {t.kind === 'TICKET' && (
          <span className="min-w-0 truncate text-[12px] font-bold text-fg">{t.subject}</span>
        )}
      </div>

      <RoutingBanner t={t} me={me} onAct={act} />
      {t.who.type === 'GUEST' && (
        <GuestBanner t={t} onSaved={() => void load()} onOpenUser={onOpenUser} />
      )}

      {t.previous.length > 0 && <PreviousConversations items={t.previous} />}

      <Timeline t={t} />

      {done ? (
        <DoneBar
          t={t}
          onReopen={() =>
            void act(
              'update',
              { status: 'OPEN', expectedVersion: t.version },
              'گفتگو دوباره باز شد',
            )
          }
        />
      ) : (
        <Composer t={t} canWrite={canWrite} onSent={() => void load().then(onChanged)} />
      )}
    </div>
  );
}

function RoutingBanner({
  t,
  me,
  onAct,
}: {
  t: ThreadView;
  me: Me;
  onAct: (p: string, b?: unknown, ok?: string) => Promise<void>;
}) {
  const now = useNow(1000);
  const r = t.routing;
  if (t.status === 'RESOLVED' || t.status === 'CLOSED') return null;
  const box = 'mx-3 mt-3 flex items-center gap-3 rounded-[18px] border p-3';
  if (r.offer?.toMe)
    return (
      <div className={cx(box, 'dk-offer border-accent bg-accent-tint')} data-banner="offer">
        <CountdownRing expiresAt={r.offer.expiresAt} size={40} />
        <p className="m-0 min-w-0 flex-1 text-[13px] font-bold">
          این گفتگو به شما پیشنهاد شده است.
        </p>
        <button
          type="button"
          onClick={() => void onAct('accept', undefined, 'گفتگو مال شما شد')}
          className="hx-press min-h-10 cursor-pointer rounded-full bg-accent px-4 text-[13px] font-extrabold text-on-accent shadow-cta"
          data-accept
        >
          پذیرش
        </button>
        <button
          type="button"
          onClick={() => void onAct('decline')}
          className="min-h-10 cursor-pointer rounded-full border border-line-strong px-3 text-[12px] font-bold text-fg-muted"
        >
          ارجاع
        </button>
      </div>
    );
  if (!r.assignee)
    return (
      <div className={cx(box, 'border-line bg-surface-1')} data-banner="queue">
        <Sparkle aria-hidden className="size-5 shrink-0 text-accent-text" />
        <p className="m-0 min-w-0 flex-1 text-[12.5px] leading-6 text-fg-muted">
          {r.offer
            ? `در حال پیشنهاد به ${r.offer.to ?? 'همکار'}…`
            : 'این گفتگو هنوز کارشناس ندارد.'}
        </p>
        <button
          type="button"
          onClick={() => void onAct('accept', undefined, 'گفتگو مال شما شد')}
          className="hx-press min-h-10 cursor-pointer rounded-full bg-accent px-4 text-[13px] font-extrabold text-on-accent shadow-cta"
          data-claim
        >
          برداشتن گفتگو
        </button>
      </div>
    );
  if (!r.mine)
    return (
      <div
        className={cx(
          box,
          r.breached ? 'border-negative bg-negative-tint' : 'border-line bg-surface-1',
        )}
        data-banner="other"
      >
        {r.breached ? (
          <AlertTriangle aria-hidden className="size-5 shrink-0 text-negative" />
        ) : (
          <Headset aria-hidden className="size-5 shrink-0 text-fg-subtle" />
        )}
        <p className="m-0 min-w-0 flex-1 text-[12.5px] leading-6">
          این گفتگو با <b>{r.assignee.name ?? 'همکار'}</b> است.
          {r.breached
            ? ' پاسخ دیر شده؛ می‌توانید آن را بردارید.'
            : ' فقط یادداشت داخلی می‌توانید بگذارید.'}
        </p>
        {(r.breached || r.broadcast || me.canSeeAll) && (
          <button
            type="button"
            onClick={() => void onAct('accept', undefined, 'گفتگو به شما سپرده شد')}
            className="hx-press min-h-10 cursor-pointer rounded-full bg-accent px-4 text-[13px] font-extrabold text-on-accent shadow-cta"
            data-takeover
          >
            برداشتن
          </button>
        )}
      </div>
    );
  if (r.slaDueAt) {
    const left = new Date(r.slaDueAt).getTime() - now;
    return (
      <div
        className={cx(
          box,
          left < 0 ? 'border-negative bg-negative-tint' : 'border-line bg-surface-1',
        )}
        data-banner="sla"
      >
        <span
          className={cx(
            'size-2.5 shrink-0 rounded-full',
            left < 0 ? 'dk-live bg-negative' : 'bg-pending',
          )}
        />
        <p className="m-0 flex-1 text-[12.5px]">
          {left >= 0
            ? `کاربر منتظر پاسخ شماست · ${span(left)} تا مهلت پاسخ`
            : `مهلت پاسخ ${span(-left)} گذشته است`}
        </p>
      </div>
    );
  }
  return null;
}

function GuestBanner({
  t,
  onSaved,
  onOpenUser,
}: {
  t: ThreadView;
  onSaved: () => void;
  onOpenUser: (id: string) => void;
}) {
  const toast = useToast();
  const [edit, setEdit] = useState(false);
  const [phone, setPhone] = useState('');
  if (t.who.type !== 'GUEST') return null;
  const w = t.who;
  const save = async () => {
    try {
      const r = await api.post<{ phoneMasked: string; matchedUserId: string | null }>(
        `/threads/${t.id}/contact-phone`,
        { phone },
      );
      toast.show({
        tone: r.matchedUserId ? 'success' : 'info',
        title: r.matchedUserId ? 'حساب این شماره پیدا شد' : 'برای این شماره حسابی پیدا نشد',
      });
      setEdit(false);
      setPhone('');
      onSaved();
    } catch (e) {
      toast.show({ tone: 'danger', title: errorMessage(e) });
    }
  };
  return (
    <div
      className="mx-3 mt-3 rounded-[18px] border border-[color-mix(in_srgb,var(--hx-pending)_45%,transparent)] bg-[color-mix(in_srgb,var(--hx-pending)_10%,var(--hx-surface-1))] p-3"
      data-guest-banner
    >
      <div className="flex items-start gap-2.5">
        <AlertTriangle aria-hidden className="mt-0.5 size-5 shrink-0 text-pending" />
        <div className="min-w-0 flex-1 text-[12.5px] leading-6">
          <p className="m-0 font-bold">
            بازدیدکننده‌ی سایت ·{' '}
            <bdi dir="ltr" className="hx-num font-mono">
              {w.phone.replace(/^\+98/, '0')}
            </bdi>{' '}
            (تأییدنشده)
          </p>
          <p className="m-0 text-fg-muted">
            {w.matchedUser
              ? `این شماره متعلق به حساب ${w.matchedUser.name ?? w.matchedUser.publicId} است، اما مالکیت آن تأیید نشده. اطلاعات حساب را بازگو نکنید و برای کارهای حساب از کاربر بخواهید وارد اپ شود.`
              : 'حسابی با این شماره در هوپکس نیست؛ ممکن است هنوز ثبت‌نام نکرده باشد.'}
          </p>
        </div>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {w.matchedUser && (
          <button
            type="button"
            onClick={() => onOpenUser(w.matchedUser?.id ?? '')}
            className="hx-press min-h-9 cursor-pointer rounded-full bg-surface-2 px-3 text-[12px] font-bold"
            data-guest-360
          >
            پرونده‌ی این شماره
          </button>
        )}
        {edit ? (
          <form
            className="flex flex-1 items-center gap-1.5"
            onSubmit={(e) => {
              e.preventDefault();
              void save();
            }}
          >
            <input
              dir="ltr"
              inputMode="tel"
              autoFocus
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="09xxxxxxxxx"
              aria-label="شماره‌ی درست موبایل"
              className="hx-num min-h-9 min-w-0 flex-1 rounded-full border border-line-strong bg-surface-2 px-3 text-[16px] outline-hidden focus-visible:border-accent"
              data-guest-phone
            />
            <button
              type="submit"
              className="min-h-9 cursor-pointer rounded-full bg-accent px-3 text-[12px] font-bold text-on-accent"
            >
              ثبت
            </button>
          </form>
        ) : (
          <button
            type="button"
            onClick={() => setEdit(true)}
            className="hx-press inline-flex min-h-9 cursor-pointer items-center gap-1 rounded-full border border-line-strong px-3 text-[12px] font-bold"
            data-guest-change
          >
            <Phone aria-hidden className="size-3.5" />
            تغییر شماره
          </button>
        )}
      </div>
    </div>
  );
}

function Timeline({ t }: { t: ThreadView }) {
  const end = useRef<HTMLDivElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const count = t.messages.length;
  const [stick, setStick] = useState(true);
  useEffect(() => {
    if (stick) end.current?.scrollIntoView({ block: 'end' });
  }, [count, stick, t.live.userTyping]);
  // The keyboard opening shrinks the visible area: keep the newest message in view.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const keep = () => {
      if (stick && box.current) box.current.scrollTop = box.current.scrollHeight;
    };
    vv.addEventListener('resize', keep);
    return () => vv.removeEventListener('resize', keep);
  }, [stick]);
  const lastAgent = [...t.messages].reverse().find((m) => m.author === 'AGENT' && !m.internal);
  const seen = !!lastAgent && !!t.live.userSeenAt && t.live.userSeenAt >= lastAgent.at;
  return (
    <div
      ref={box}
      onScroll={(e) => {
        const el = e.currentTarget;
        setStick(el.scrollHeight - el.scrollTop - el.clientHeight < 80);
      }}
      className="relative min-h-0 flex-1 overflow-y-auto px-3 py-4"
      aria-live="polite"
      data-timeline
    >
      <p className="m-0 mb-4 text-center text-[11px] text-fg-subtle">
        شروع گفتگو · {formatJalali(t.createdAt)}
      </p>
      <ol className="m-0 flex list-none flex-col gap-1.5 p-0">
        {t.messages.map((m, i) => (
          <Bubble
            key={m.id}
            m={m}
            prev={t.messages[i - 1]}
            file={t.attachments.find((a) => a.messageId === m.id)}
          />
        ))}
        {t.live.userTyping && (
          <li className="dk-bubble-in mt-2 self-start rounded-[18px] rounded-ss-[6px] border border-line bg-surface-1 px-3.5 py-2.5">
            <TypingDots label="کاربر در حال نوشتن…" />
          </li>
        )}
        {lastAgent && (
          <li className="self-end px-1 text-[10.5px] text-fg-subtle" data-seen={seen}>
            {seen ? (
              <span className="inline-flex items-center gap-1 text-accent-text">
                <CheckCheck aria-hidden className="size-3.5" /> دیده شد
              </span>
            ) : (
              <span className="inline-flex items-center gap-1">
                <Check aria-hidden className="size-3.5" /> ارسال شد
              </span>
            )}
          </li>
        )}
        {t.rating && (
          <li
            className="mt-3 self-center rounded-full border border-line bg-surface-1 px-3 py-1.5 text-[12px]"
            data-rating={t.rating.score}
          >
            امتیاز کاربر: {'★'.repeat(t.rating.score)}
            {'☆'.repeat(5 - t.rating.score)}
            {t.rating.comment && <span className="text-fg-muted"> — {t.rating.comment}</span>}
          </li>
        )}
      </ol>
      <div ref={end} />
      {!stick && (
        <button
          type="button"
          onClick={() => {
            setStick(true);
            end.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
          }}
          className="sticky bottom-2 ms-auto grid size-10 cursor-pointer place-items-center rounded-full border border-line-strong bg-surface-2 shadow-float"
          aria-label="رفتن به آخرین پیام"
        >
          <ChevronDown aria-hidden className="size-5" />
        </button>
      )}
    </div>
  );
}

function Bubble({
  m,
  prev,
  file,
}: {
  m: Message;
  prev: Message | undefined;
  file: Attachment | undefined;
}) {
  if (m.author === 'SYSTEM' && m.kind === 'GREETING')
    return (
      <li className="dk-bubble-in my-2 flex flex-col items-end" data-msg="greeting">
        <span className="mb-1 inline-flex items-center gap-1 text-[10.5px] font-semibold text-fg-subtle">
          <Sparkle aria-hidden className="size-3" /> پیام خودکار خوش‌آمد
        </span>
        <div className="max-w-[85%] rounded-[18px] rounded-se-[6px] border border-dashed border-line-strong bg-surface-1 px-3.5 py-2 text-[13px] leading-7 text-fg-muted">
          {m.body}
        </div>
      </li>
    );
  if (m.author === 'SYSTEM')
    return (
      <li
        className="my-1.5 self-center rounded-full bg-surface-2 px-3 py-1 text-center text-[11px] leading-6 text-fg-muted"
        data-msg="notice"
      >
        {m.body}
      </li>
    );
  const user = m.author === 'USER';
  const first = !prev || prev.author !== m.author || prev.internal !== m.internal;
  return (
    <li
      className={cx(
        'dk-bubble-in flex flex-col',
        user ? 'items-start' : 'items-end',
        first && 'mt-2',
      )}
      data-msg={m.internal ? 'note' : m.author}
    >
      {first && !user && (
        <span className="mb-1 inline-flex items-center gap-1 text-[10.5px] font-semibold text-fg-subtle">
          {m.internal ? (
            <Lock aria-hidden className="size-3" />
          ) : (
            <Headset aria-hidden className="size-3" />
          )}
          {m.internal
            ? `یادداشت داخلی · ${m.agent ?? ''}`
            : m.mine
              ? 'شما'
              : (m.agent ?? 'کارشناس')}
        </span>
      )}
      <div
        className={cx(
          'max-w-[85%] px-3.5 py-2 text-[13.5px] leading-7',
          user
            ? 'rounded-[18px] rounded-ss-[6px] border border-line bg-surface-1 text-fg'
            : m.internal
              ? 'rounded-[18px] rounded-se-[6px] border border-[color-mix(in_srgb,var(--hx-pending)_45%,transparent)] bg-[color-mix(in_srgb,var(--hx-pending)_12%,var(--hx-surface-1))] text-fg'
              : 'rounded-[18px] rounded-se-[6px] bg-accent text-on-accent',
        )}
        title={formatJalali(m.at)}
      >
        {file ? (
          <FileView a={file} />
        ) : (
          <p className="m-0 whitespace-pre-line [overflow-wrap:anywhere]">{m.body}</p>
        )}
        {m.redacted && (
          <p className="m-0 mt-1 text-[10.5px] opacity-80">
            بخشی از پیام (رمز یا کد) برای امنیت پوشانده شد.
          </p>
        )}
      </div>
      <span className="mt-0.5 px-1 text-[10.5px] text-fg-subtle">{formatRelative(m.at)}</span>
    </li>
  );
}

function FileView({ a }: { a: Attachment }) {
  const [url, setUrl] = useState<string | null>(null);
  const isImage = a.contentType.startsWith('image/');
  const open = async () => {
    try {
      const res = await fetch(`/api/v1/desk/attachments/${a.id}`, {
        credentials: 'same-origin',
        cache: 'no-store',
      });
      if (!res.ok) throw new Error('file');
      const u = URL.createObjectURL(await res.blob());
      setUrl(u);
      if (!isImage) window.open(u, '_blank', 'noopener');
    } catch {
      setUrl(null);
    }
  };
  useEffect(() => () => (url ? URL.revokeObjectURL(url) : undefined), [url]);
  return isImage && url ? (
    <img src={url} alt={a.fileName ?? 'تصویر پیوست'} className="max-h-72 rounded-[12px]" />
  ) : (
    <button
      type="button"
      onClick={() => void open()}
      className="inline-flex cursor-pointer items-center gap-2 text-start underline-offset-4 hover:underline"
    >
      <FileText aria-hidden className="size-5 shrink-0" />
      <span className="min-w-0 truncate">{a.fileName ?? 'فایل پیوست'}</span>
      <span className="hx-num shrink-0 text-[11px] opacity-75">
        {Math.max(1, Math.round(a.byteSize / 1024)).toLocaleString('fa-IR')} KB
      </span>
    </button>
  );
}

function Composer({
  t,
  canWrite,
  onSent,
}: {
  t: ThreadView;
  canWrite: boolean;
  onSent: () => void;
}) {
  const toast = useToast();
  const [text, setText] = useState('');
  const [noteChoice, setNote] = useState(false);
  // A colleague's conversation takes notes only (derived, so it follows ownership changes live).
  const note = !canWrite || noteChoice;
  const [busy, setBusy] = useState(false);
  const [canned, setCanned] = useState(false);
  const area = useRef<HTMLTextAreaElement>(null);
  const file = useRef<HTMLInputElement>(null);
  const lastTyping = useRef(0);
  const coarse = window.matchMedia('(pointer: coarse)').matches;

  const send = async (resolve = false) => {
    const body = text.trim();
    if (!body || busy) return;
    setBusy(true);
    try {
      await api.post(`/threads/${t.id}/reply`, { body, internal: note, resolve: resolve && !note });
      setText('');
      if (area.current) area.current.style.height = 'auto';
      onSent();
    } catch (e) {
      toast.show({ tone: 'danger', title: errorMessage(e) });
    } finally {
      setBusy(false);
    }
  };
  const upload = async (f: File) => {
    try {
      await api.upload(`/threads/${t.id}/attachments`, f);
      onSent();
    } catch (e) {
      toast.show({ tone: 'danger', title: errorMessage(e) });
    }
  };

  return (
    <footer
      className="shrink-0 border-t border-line bg-surface-1 px-3 pb-[calc(var(--hx-sab,env(safe-area-inset-bottom))+10px)] pt-2.5"
      data-composer
    >
      {canned && (
        <div className="dk-enter mb-2 flex flex-wrap gap-1.5" data-canned>
          {CANNED.map((c) => (
            <button
              key={c.label}
              type="button"
              onClick={() => {
                setText(c.text);
                setCanned(false);
                area.current?.focus();
              }}
              className="hx-press min-h-8 cursor-pointer rounded-full border border-line bg-surface-2 px-3 text-[12px] font-semibold text-fg-muted hover:border-accent hover:text-fg"
            >
              {c.label}
            </button>
          ))}
        </div>
      )}
      <div className="mb-2 flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => setNote(false)}
          disabled={!canWrite}
          aria-pressed={!note}
          className={cx(
            'min-h-8 cursor-pointer rounded-full px-3 text-[12px] font-bold disabled:cursor-not-allowed disabled:opacity-40',
            !note ? 'bg-accent text-on-accent' : 'bg-surface-2 text-fg-muted',
          )}
          data-mode="reply"
        >
          پاسخ به کاربر
        </button>
        <button
          type="button"
          onClick={() => setNote(true)}
          aria-pressed={note}
          className={cx(
            'inline-flex min-h-8 cursor-pointer items-center gap-1 rounded-full px-3 text-[12px] font-bold',
            note ? 'bg-pending text-white' : 'bg-surface-2 text-fg-muted',
          )}
          data-mode="note"
        >
          <Lock aria-hidden className="size-3.5" /> یادداشت داخلی
        </button>
        <button
          type="button"
          onClick={() => setCanned((v) => !v)}
          aria-expanded={canned}
          className="ms-auto min-h-8 cursor-pointer rounded-full bg-surface-2 px-3 text-[12px] font-bold text-fg-muted hover:text-fg"
          data-canned-toggle
        >
          پاسخ آماده
        </button>
      </div>
      <div className="flex items-end gap-2">
        <button
          type="button"
          onClick={() => file.current?.click()}
          aria-label="پیوست فایل"
          className="grid size-11 shrink-0 cursor-pointer place-items-center rounded-full bg-surface-2 text-fg-muted hover:text-fg"
          data-attach
        >
          <Paperclip aria-hidden className="size-5" />
        </button>
        <input
          ref={file}
          type="file"
          hidden
          accept="image/jpeg,image/png,application/pdf,video/mp4,video/webm,video/quicktime"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void upload(f);
            e.target.value = '';
          }}
        />
        <textarea
          ref={area}
          value={text}
          rows={1}
          maxLength={4000}
          onChange={(e) => {
            setText(e.target.value);
            e.target.style.height = 'auto';
            e.target.style.height = `${String(Math.min(e.target.scrollHeight, 160))}px`;
            if (!note && Date.now() - lastTyping.current > 3_000) {
              lastTyping.current = Date.now();
              void api.post(`/threads/${t.id}/typing`).catch(() => undefined);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !coarse) {
              e.preventDefault();
              void send();
            }
          }}
          placeholder={note ? 'یادداشت برای همکاران (کاربر نمی‌بیند)…' : 'پاسخ خود را بنویسید…'}
          aria-label={note ? 'یادداشت داخلی' : 'پاسخ به کاربر'}
          className={cx(
            'max-h-40 min-h-11 flex-1 resize-none rounded-[18px] border px-3.5 py-2.5 text-[16px] leading-6 text-fg outline-hidden placeholder:text-fg-subtle',
            note
              ? 'border-[color-mix(in_srgb,var(--hx-pending)_55%,transparent)] bg-[color-mix(in_srgb,var(--hx-pending)_8%,var(--hx-surface-2))]'
              : 'border-line-strong bg-surface-2 focus-visible:border-accent',
          )}
          data-composer-input
        />
        <button
          type="button"
          onClick={() => void send()}
          disabled={!text.trim() || busy}
          aria-label="ارسال"
          className="hx-press grid size-11 shrink-0 cursor-pointer place-items-center rounded-full bg-accent text-on-accent shadow-cta disabled:cursor-not-allowed disabled:opacity-40"
          data-send
        >
          <SendHorizontal aria-hidden className="size-5 -scale-x-100" />
        </button>
      </div>
      {!note && (
        <div className="mt-2 flex items-center justify-between gap-2">
          <span className="text-[10.5px] text-fg-subtle">
            {coarse ? '' : 'Enter = ارسال · Shift+Enter = خط جدید'}
          </span>
          <button
            type="button"
            onClick={() => void send(true)}
            disabled={!text.trim() || busy}
            className="inline-flex min-h-8 cursor-pointer items-center gap-1 rounded-full border border-line-strong px-3 text-[12px] font-bold text-positive disabled:cursor-not-allowed disabled:opacity-40"
            data-send-resolve
          >
            <CheckCheck aria-hidden className="size-4" /> ارسال و حل شد
          </button>
        </div>
      )}
    </footer>
  );
}

/**
 * Owner 2026-10-16: the same person's earlier conversations (by account or the same mobile), folded
 * under the identity strip; tap one to read it in full.
 */
function PreviousConversations({ items }: { items: ThreadView['previous'] }) {
  return (
    <details
      className="group shrink-0 border-b border-line bg-surface-1 px-3 py-2"
      data-previous={items.length}
    >
      <summary className="flex min-h-9 cursor-pointer list-none items-center gap-2 text-[12.5px] font-bold text-fg [&::-webkit-details-marker]:hidden">
        <History aria-hidden className="size-4 text-accent-text" />
        گفتگوهای قبلی همین شخص
        <span className="hx-num grid min-w-5 place-items-center rounded-full bg-surface-3 px-1.5 text-[11px]">
          {items.length.toLocaleString('fa-IR')}
        </span>
        <ChevronDown
          aria-hidden
          className="ms-auto size-4 text-fg-subtle transition-transform group-open:rotate-180"
        />
      </summary>
      <ul className="m-0 mt-2 flex max-h-[38dvh] list-none flex-col gap-1.5 overflow-y-auto p-0 pb-1">
        {items.map((p) => (
          <li key={p.id}>
            <a
              href={`/t/${p.id}`}
              onClick={(e) => {
                e.preventDefault();
                navigate(`/t/${p.id}`);
              }}
              className="hx-press flex flex-col gap-1 rounded-[14px] border border-line bg-surface-2 px-3 py-2 outline-hidden hover:border-line-strong focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-focus"
              data-previous-item={p.ref}
            >
              <span className="flex flex-wrap items-center gap-1.5 text-[11px] text-fg-subtle">
                <KindBadge kind={p.kind} />
                <span>{formatJalali(p.createdAt)}</span>
                <span aria-hidden>·</span>
                <span>{STATUS_FA[p.status] ?? p.status}</span>
                <span aria-hidden>·</span>
                <span className="hx-num">{p.messages.toLocaleString('fa-IR')} پیام</span>
                <bdi dir="ltr" className="ms-auto font-mono">
                  {p.ref}
                </bdi>
              </span>
              <span className="line-clamp-2 text-[12.5px] leading-6 text-fg-muted">
                {p.preview ?? (p.kind === 'TICKET' ? p.subject : CATEGORY_FA[p.category])}
              </span>
            </a>
          </li>
        ))}
      </ul>
    </details>
  );
}

function DoneBar({ t, onReopen }: { t: ThreadView; onReopen: () => void }) {
  const chat = t.kind !== 'TICKET';
  return (
    <footer
      className="flex shrink-0 items-center gap-2 border-t border-line bg-surface-1 px-4 pb-[calc(var(--hx-sab,env(safe-area-inset-bottom))+12px)] pt-3"
      data-done
    >
      <Star aria-hidden className="size-4 text-fg-subtle" />
      <p className="m-0 flex-1 text-[12.5px] text-fg-muted">
        این گفتگو {t.status === 'CLOSED' ? 'بسته شده' : 'حل شده'} است.
        {chat
          ? ' اگر کاربر دوباره پیام بدهد، گفتگوی تازه‌ای شروع می‌شود.'
          : t.reopenUntil && t.status === 'RESOLVED'
            ? ` اگر کاربر تا ${formatJalali(t.reopenUntil)} پیام بدهد، دوباره باز می‌شود.`
            : ''}
      </p>
      <button
        type="button"
        onClick={onReopen}
        className="min-h-9 cursor-pointer rounded-full border border-line-strong px-3 text-[12px] font-bold"
      >
        بازکردن دوباره
      </button>
    </footer>
  );
}

function ThreadMenu({
  t,
  onAct,
}: {
  t: ThreadView;
  onAct: (p: string, b?: unknown, ok?: string) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [colleagues, setColleagues] = useState<
    { id: string; name: string; online: boolean }[] | null
  >(null);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const f = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', f);
    return () => document.removeEventListener('pointerdown', f);
  }, [open]);
  const item =
    'flex min-h-10 w-full cursor-pointer items-center gap-2 rounded-[12px] px-3 text-start text-[13px] hover:bg-surface-2';
  const v = { expectedVersion: t.version };
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-label="کارهای بیشتر"
        aria-expanded={open}
        onClick={() => setOpen((x) => !x)}
        className="hx-press grid size-10 cursor-pointer place-items-center rounded-full text-fg-muted hover:bg-surface-2"
        data-thread-menu
      >
        <MoreHorizontal aria-hidden className="size-5" />
      </button>
      {open && (
        <div
          className="dk-enter absolute end-0 top-12 z-50 max-h-[70dvh] w-64 overflow-y-auto rounded-[18px] border border-line-strong bg-surface-2 p-1.5 shadow-float"
          role="menu"
        >
          {t.status !== 'RESOLVED' && t.status !== 'CLOSED' && (
            <button
              type="button"
              role="menuitem"
              className={item}
              onClick={() =>
                void onAct('update', { status: 'RESOLVED', ...v }, 'حل شد').then(() =>
                  setOpen(false),
                )
              }
            >
              <CheckCheck aria-hidden className="size-4 text-positive" /> علامت «حل شد»
            </button>
          )}
          {t.status !== 'CLOSED' && (
            <button
              type="button"
              role="menuitem"
              className={item}
              onClick={() =>
                void onAct('update', { status: 'CLOSED', ...v }, 'بسته شد').then(() =>
                  setOpen(false),
                )
              }
            >
              <Lock aria-hidden className="size-4" /> بستن گفتگو
            </button>
          )}
          {t.routing.mine && (
            <>
              <button
                type="button"
                role="menuitem"
                className={item}
                onClick={() =>
                  void onAct('release', undefined, 'به صف برگشت').then(() => setOpen(false))
                }
              >
                <Headset aria-hidden className="size-4" /> برگرداندن به صف
              </button>
              <button
                type="button"
                role="menuitem"
                className={item}
                onClick={() =>
                  void api
                    .get<{ items: { id: string; name: string; online: boolean }[] }>('/colleagues')
                    .then((r) => setColleagues(r.items))
                }
              >
                <UserRoundSearch aria-hidden className="size-4" /> انتقال به همکار
              </button>
              {colleagues && (
                <div className="mx-1 mb-1 rounded-[12px] bg-surface-1 p-1">
                  {colleagues.length === 0 && (
                    <p className="m-0 p-2 text-[12px] text-fg-muted">
                      همکار دیگری با دستگاه فعال نیست.
                    </p>
                  )}
                  {colleagues.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      className={item}
                      onClick={() =>
                        void onAct('transfer', { to: c.id }, `به ${c.name} پیشنهاد شد`).then(() =>
                          setOpen(false),
                        )
                      }
                    >
                      <span
                        className={cx(
                          'size-2 rounded-full',
                          c.online ? 'bg-positive' : 'bg-fg-subtle',
                        )}
                      />{' '}
                      {c.name}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
          <p className="m-0 px-3 pt-2 text-[11px] font-semibold text-fg-subtle">اولویت</p>
          <div className="flex gap-1 p-1">
            {(['LOW', 'NORMAL', 'HIGH', 'URGENT'] as const).map((p) => (
              <button
                key={p}
                type="button"
                aria-pressed={t.priority === p}
                onClick={() =>
                  void onAct('update', { priority: p, ...v }).then(() => setOpen(false))
                }
                className={cx(
                  'min-h-8 flex-1 cursor-pointer rounded-full text-[11.5px] font-bold',
                  t.priority === p ? 'bg-accent text-on-accent' : 'bg-surface-1 text-fg-muted',
                )}
              >
                {PRIORITY_FA[p]}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
